import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  School,
  Search,
  Edit,
  Trash2,
  Users,
  TrendingUp,
  BookOpen,
  Plus,
  ArrowRight,
  GraduationCap,
  RefreshCw,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import ClassRegistrationForm from "@/components/forms/ClassRegistrationForm";
import ClassEditForm from "@/components/forms/ClassEditForm";
import PromoteStudentsForm from "@/components/forms/PromoteStudentsForm";
import StudentsTableView from "@/components/StudentsTableView";
import { db } from "../firebase/config";
import {
  collection,
  onSnapshot,
  getDocs,
  query,
  where,
  Timestamp,
  doc,
  setDoc,
  deleteDoc,
  updateDoc,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { SidebarProvider } from "@/components/ui/sidebar";
import Header from "./Header";
import AdminSidebar from "./Sidebar";

interface Affiliation {
  role: string;
  schoolID?: string;
}

interface ClassItem {
  id: string;
  level: number;
  name: string;
  stream: string;
  capacity: number;
  enrolled: number;
  academicYear: string;
  schoolID: string;
  submittedBy: string;
  classUid: string;
  createdAt: Date;
  namingConvention: string;
  stageType?: string;
  roomNumber?: string;
  teacher?: string | null;
  teacherId?: string | null;
  currentClassTeacherUid?: string | null;
  bridgeToGrade?: string;
  bridgeToConvention?: string;
}

interface Stream {
  id: string;
  classUid: string;
  name: string;
  roomNumber: string;
  capacity: number;
  enrolled: number;
  teacher: string | null;
  teacherId: string | null;
  currentClassTeacherUid: string | null;
  isGraduated: boolean;
}

interface Grade {
  grade: string;
  level: number;
  conventionId: string;
  isTerminal: boolean;
  nextGrade?: string;
  bridgeToGrade?: string;
  bridgeToConvention?: string;
  isGraduated: boolean;
  streams: Stream[];
}

interface NamingConvention {
  id: string;
  name: string;
  prefix: string;
  startLevel: number;
  endLevel: number;
  isActive: boolean;
}

const ClassesManagement = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeView, setActiveView] = useState<"list" | "register" | "edit" | "students" | "promote">("list");
  const [selectedClass, setSelectedClass] = useState<{ grade: string; stream: Stream } | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterGrade, setFilterGrade] = useState("all");
  const [filterStream, setFilterStream] = useState("all");
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [enrollmentLoading, setEnrollmentLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAddStreamDialogOpen, setIsAddStreamDialogOpen] = useState(false);
  const [newStreamName, setNewStreamName] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ gradeIndex: number; streamIndex: number; stream: Stream } | null>(null);

  const [classHierarchy, setClassHierarchy] = useState<Grade[]>([]);
  const [namingConventions, setNamingConventions] = useState<NamingConvention[]>([]);
  const [activeConvention, setActiveConvention] = useState<NamingConvention | null>(null);

  /* ── Auth & Data Fetching ──────────────────────────────────────── */
  useEffect(() => {
    let unsubscribeAuth: (() => void) | undefined;
    let unsubscribeClasses: (() => void) | undefined;

    const auth = getAuth();
    unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setLoading(false);
        // Don't redirect here - let App.tsx global routing handle it
        // This prevents incorrect redirects during network errors
        console.log("ClassesManagement: No user found, deferring to App.tsx routing");
        return;
      }

      try {
        const idTokenResult = await user.getIdTokenResult(true);
        const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
        const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;
        const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

        let selectedAffiliation = defaultAffiliation && defaultAffiliation.role === "admin" && defaultAffiliation.schoolID
          ? defaultAffiliation
          : affiliations.find((aff) => aff.role === "admin" && aff.schoolID) || null;

        if (!selectedAffiliation || selectedAffiliation.schoolID !== claimSchoolID) {
          const retryAffiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
          selectedAffiliation = retryAffiliations.find((aff) => aff.role === "admin" && aff.schoolID) || null;

          if (!selectedAffiliation || selectedAffiliation.schoolID !== claimSchoolID) {
            throw new Error(
              `Invalid token claims: affiliations=${JSON.stringify(retryAffiliations)}, defaultAffiliation=${JSON.stringify(
                defaultAffiliation
              )}, schoolID=${claimSchoolID}`
            );
          }
        }

        setSchoolID(selectedAffiliation.schoolID || null);
        if (selectedAffiliation.schoolID) {
          const classesCollectionRef = collection(db, `schools/${selectedAffiliation.schoolID}/classes`);
          unsubscribeClasses = onSnapshot(classesCollectionRef, async (snapshot) => {
            try {
              const classesData: ClassItem[] = snapshot.docs.map(doc => ({
                id: doc.id,
                level: doc.data().level,
                name: doc.data().name,
                stream: doc.data().stream,
                capacity: doc.data().capacity || 0,
                enrolled: 0, // Placeholder, updated later
                academicYear: doc.data().academicYear,
                schoolID: doc.data().schoolID,
                submittedBy: doc.data().submittedBy,
                classUid: doc.data().classUid,
                createdAt: doc.data().createdAt instanceof Timestamp ? doc.data().createdAt.toDate() : doc.data().createdAt,
                namingConvention: doc.data().namingConvention,
                stageType: doc.data().stageType,
                roomNumber: doc.data().roomNumber || "",
                teacher: doc.data().teacher || null,
                teacherId: doc.data().teacherId || null,
                currentClassTeacherUid: doc.data().currentClassTeacherUid || null,
                bridgeToGrade: doc.data().bridgeToGrade,
                bridgeToConvention: doc.data().bridgeToConvention,
              }));

              const hierarchy: Grade[] = [];
              const conventions: NamingConvention[] = [];
              const conventionMap = new Map<string, { startLevel: number; endLevel: number }>();

              classesData.forEach((classItem) => {
                const gradeName = `${classItem.namingConvention} ${classItem.level}`;
                let grade = hierarchy.find((g) => g.grade === gradeName);
                if (!grade) {
                  const stageType = classItem.stageType;
                  const isTerminal = stageType === "final";
                  let nextGradeStr = !isTerminal ? `${classItem.namingConvention} ${classItem.level + 1}` : undefined;
                  const bridgeToGradeStr = isTerminal ? undefined : classItem.bridgeToGrade;
                  const bridgeToConventionStr = isTerminal ? undefined : classItem.bridgeToConvention;
                  if (bridgeToGradeStr) {
                    nextGradeStr = bridgeToGradeStr;
                  }
                  grade = {
                    grade: gradeName,
                    level: classItem.level,
                    conventionId: classItem.namingConvention,
                    isTerminal,
                    nextGrade: nextGradeStr,
                    bridgeToGrade: bridgeToGradeStr,
                    bridgeToConvention: bridgeToConventionStr,
                    isGraduated: false,
                    streams: [],
                  };
                  hierarchy.push(grade);
                }

                grade.streams.push({
                  id: classItem.id,
                  classUid: classItem.classUid,
                  name: classItem.stream,
                  roomNumber: classItem.roomNumber || "",
                  capacity: classItem.capacity || 0,
                  enrolled: classItem.enrolled,
                  teacher: classItem.teacher || null,
                  teacherId: classItem.teacherId || null,
                  currentClassTeacherUid: classItem.currentClassTeacherUid || null,
                  isGraduated: false,
                });

                if (!conventionMap.has(classItem.namingConvention)) {
                  conventionMap.set(classItem.namingConvention, {
                    startLevel: classItem.level,
                    endLevel: classItem.level,
                  });
                } else {
                  const conv = conventionMap.get(classItem.namingConvention)!;
                  conv.startLevel = Math.min(conv.startLevel, classItem.level);
                  conv.endLevel = Math.max(conv.endLevel, classItem.level);
                }
              });

              conventionMap.forEach((value, name) => {
                conventions.push({
                  id: name,
                  name,
                  prefix: name,
                  startLevel: value.startLevel,
                  endLevel: value.endLevel,
                  isActive: name === "Grade",
                });
              });

              setClassHierarchy(hierarchy.sort((a, b) => a.level - b.level));
              setNamingConventions(conventions);
              setActiveConvention(conventions.length > 0 ? (conventions.find((c) => c.isActive) || conventions[0]) : null);
              setLoading(false);

              // Fetch enrollment counts asynchronously
              if (classesData.length > 0) {
                setEnrollmentLoading(true);
                const classUids = classesData.map(c => c.classUid);
                console.log("Fetching enrollments for classUids:", classUids); // Debug log
                const enrollmentCounts = await fetchEnrollments(classUids, selectedAffiliation.schoolID!);
                console.log("Enrollment counts:", enrollmentCounts); // Debug log
                setClassHierarchy(prev => prev.map(grade => ({
                  ...grade,
                  streams: grade.streams.map(stream => ({
                    ...stream,
                    enrolled: enrollmentCounts[stream.classUid] || 0,
                    capacity: classesData.find(c => c.classUid === stream.classUid)?.capacity || 0,
                  })),
                })));
                setEnrollmentLoading(false);
              }
            } catch (error) {
              console.error("Error fetching classes:", error);
              setError("Failed to fetch classes. Please retry.");
              setLoading(false);
              toast({
                title: "Error Loading Classes",
                description: "Failed to fetch classes. Please try again.",
                variant: "destructive",
                duration: 5000,
              });
            }
          }, (error) => {
            console.error("Error listening to classes:", error);
            setError("Failed to fetch classes. Please retry.");
            setLoading(false);
            toast({
              title: "Error Loading Classes",
              description: "Failed to fetch classes. Please try again.",
              variant: "destructive",
              duration: 5000,
            });
          });
        }
      } catch (error: any) {
        console.error("Error verifying user claims:", error);
        setLoading(false);
        
        // Check if it's a network error
        const isNetworkError = error?.code === 'auth/network-request-failed' ||
                              error?.message?.includes('ERR_NAME_NOT_RESOLVED') ||
                              error?.message?.includes('network-request-failed') ||
                              error?.message?.includes('network');
        
        if (isNetworkError) {
          // Don't logout on network errors, just show a warning
          setError("Network error. Please check your connection.");
          toast({
            title: "Network Error",
            description: "Unable to verify permissions due to network issues. Please check your connection.",
            variant: "destructive",
            duration: 5000,
          });
        } else {
          // Only logout for non-network errors
          setError("Failed to verify permissions. Please contact support.");
          toast({
            title: "Missing Permissions",
            description: "You lack the necessary permissions to manage classes. Please contact support.",
            variant: "destructive",
            duration: 5000,
          });
          navigate("/login-page");
        }
      }
    });

    return () => {
      unsubscribeAuth?.();
      unsubscribeClasses?.();
    };
  }, [navigate, toast]);

  const fetchEnrollments = useCallback(async (classUids: string[], schoolId: string): Promise<{ [key: string]: number }> => {
    if (classUids.length === 0) return {};
    const studentsRef = collection(db, `schools/${schoolId}/students`);
    const results: { [key: string]: number } = {};
    classUids.forEach(uid => results[uid] = 0);

    // Handle in batches of 10 due to Firestore 'in' query limit
    for (let i = 0; i < classUids.length; i += 10) {
      const batchUids = classUids.slice(i, i + 10);
      const batchQuery = query(studentsRef, where("currentClassUid", "in", batchUids));
      const snapshot = await getDocs(batchQuery);
      snapshot.forEach(doc => {
        const classUid = doc.data().currentClassUid;
        if (classUid) {
          results[classUid] = (results[classUid] || 0) + 1;
        }
      });
    }
    return results;
  }, []);

  const saveBridgeToFirestore = async (gradeIndex: number, stageType: string | undefined, bridgeToGrade: string | undefined, bridgeToConvention: string | undefined) => {
    const grade = classHierarchy[gradeIndex];
    if (!schoolID) return;

    try {
      const classesCollectionRef = collection(db, `schools/${schoolID}/classes`);

      for (const stream of grade.streams) {
        const classRef = doc(classesCollectionRef, stream.id);
        const updateData: any = { stageType };
        if (bridgeToGrade !== undefined) updateData.bridgeToGrade = bridgeToGrade;
        if (bridgeToConvention !== undefined) updateData.bridgeToConvention = bridgeToConvention;
        await updateDoc(classRef, updateData);
      }

      toast({
        title: "Bridge Updated",
        description: `Bridge settings updated for ${grade.grade}.`,
        duration: 3000,
      });
    } catch (error: any) {
      console.error("Error saving bridge:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save bridge settings.",
      });
    }
  };

  /* ── Handlers ──────────────────────────────────────────────────── */
  const handleRegisterClass = () => {
    setActiveView("register");
  };

  const handleEditStream = (grade: string, stream: Stream) => {
    setSelectedClass({ grade, stream });
    setActiveView("edit");
  };

  const handleViewStudents = (grade: string, stream: Stream) => {
    console.log("Navigating to StudentsTableView with classUid:", stream.classUid);
    setSelectedClass({ grade, stream });
    setActiveView("students");
  };

  const handleGraduateStream = (gradeIndex: number, streamIndex: number) => {
    const updatedHierarchy = [...classHierarchy];
    updatedHierarchy[gradeIndex].streams[streamIndex].isGraduated = true;
    setClassHierarchy(updatedHierarchy);
  };

  const handleGraduateGrade = (gradeIndex: number) => {
    const updatedHierarchy = [...classHierarchy];
    updatedHierarchy[gradeIndex].isGraduated = true;
    updatedHierarchy[gradeIndex].streams.forEach((stream) => {
      stream.isGraduated = true;
    });
    setClassHierarchy(updatedHierarchy);
  };

  const handleAddStream = async (_gradeIndex: number) => {
    if (!schoolID || !activeConvention) {
      toast({
        title: "Error",
        description: "School ID or active convention missing. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }
    setIsAddStreamDialogOpen(true);
  };

  const handleAddStreamSubmit = async () => {
    if (!schoolID || !activeConvention) {
      toast({
        title: "Error",
        description: "School ID or active convention missing. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    const streamName = newStreamName.trim();
    if (!streamName) {
      toast({
        title: "Invalid Stream Name",
        description: "Please enter a valid stream name.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        toast({
          title: "Authentication Error",
          description: "User is not authenticated. Please log in again.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
        return;
      }

      const updatedHierarchy = [...classHierarchy];
      const classesCollectionRef = collection(db, `schools/${schoolID}/classes`);

      for (const grade of updatedHierarchy) {
        if (grade.conventionId !== activeConvention.id) continue;
        const fullClassName = `${grade.grade} ${streamName}`;
        const q = query(classesCollectionRef, where("name", "==", fullClassName));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          toast({
            title: "Stream Already Exists",
            description: `A stream named "${streamName}" already exists in ${grade.grade}.`,
            variant: "destructive",
            duration: 5000,
          });
          return;
        }
      }

      for (const grade of updatedHierarchy) {
        if (grade.conventionId !== activeConvention.id) continue;

        const fullClassName = `${grade.grade} ${streamName}`;
        const classDocRef = doc(classesCollectionRef);
        const newClass: ClassItem = {
          id: classDocRef.id,
          level: grade.level,
          name: fullClassName,
          stream: streamName,
          capacity: 30, // Default capacity, adjustable in ClassEditForm
          enrolled: 0,
          academicYear: new Date().getFullYear().toString(),
          schoolID,
          submittedBy: user.uid,
          classUid: classDocRef.id,
          createdAt: new Date(),
          namingConvention: activeConvention.name,
          stageType: isLastLevel(grade) ? "final" : undefined,
          roomNumber: "",
          teacher: null,
          teacherId: null,
          currentClassTeacherUid: null,
        };

        await setDoc(classDocRef, {
          level: newClass.level,
          name: newClass.name,
          stream: newClass.stream,
          capacity: newClass.capacity,
          enrolled: newClass.enrolled,
          academicYear: newClass.academicYear,
          schoolID: newClass.schoolID,
          submittedBy: newClass.submittedBy,
          classUid: newClass.classUid,
          createdAt: newClass.createdAt,
          namingConvention: newClass.namingConvention,
          ...(isLastLevel(grade) ? { stageType: newClass.stageType } : {}),
          roomNumber: newClass.roomNumber,
          teacher: newClass.teacher,
          teacherId: newClass.teacherId,
          currentClassTeacherUid: newClass.currentClassTeacherUid,
        });

        grade.streams.push({
          id: classDocRef.id,
          classUid: classDocRef.id,
          name: streamName,
          roomNumber: "",
          capacity: newClass.capacity,
          enrolled: 0,
          teacher: null,
          teacherId: null,
          currentClassTeacherUid: null,
          isGraduated: false,
        });
      }

      setClassHierarchy(updatedHierarchy);
      setIsAddStreamDialogOpen(false);
      setNewStreamName("");

      toast({
        title: "Stream Added Successfully",
        description: `Stream "${streamName}" has been added to all levels in the ${activeConvention.name} system.`,
        duration: 3000,
      });
    } catch (error: any) {
      console.error("Error adding stream:", error);
      toast({
        title: "Error Adding Stream",
        description: `Failed to add stream: ${error.message || "An unexpected error occurred."}`,
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleDeleteStream = async (gradeIndex: number, streamIndex: number) => {
    if (!schoolID) {
      toast({
        title: "Error",
        description: "School ID is missing. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    const stream = classHierarchy[gradeIndex].streams[streamIndex];
    setDeleteTarget({ gradeIndex, streamIndex, stream });
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteStream = async () => {
    if (!deleteTarget || !schoolID) return;

    const { gradeIndex, streamIndex, stream } = deleteTarget;

    try {
      const classRef = doc(db, `schools/${schoolID}/classes`, stream.id);
      await deleteDoc(classRef);

      const studentsRef = collection(db, `schools/${schoolID}/students`);
      const q = query(studentsRef, where("currentClassUid", "==", stream.classUid));
      const querySnapshot = await getDocs(q);

      for (const studentDoc of querySnapshot.docs) {
        await updateDoc(doc(studentsRef, studentDoc.id), {
          currentClassUid: null,
          className: null,
        });
      }

      const updatedHierarchy = [...classHierarchy];
      updatedHierarchy[gradeIndex].streams.splice(streamIndex, 1);
      setClassHierarchy(updatedHierarchy);

      toast({
        title: "Stream Deleted",
        description: `Stream ${stream.name} in ${classHierarchy[gradeIndex].grade} has been deleted. Affected students have been unassigned from this class.`,
        duration: 3000,
      });
    } catch (error: any) {
      console.error("Error deleting stream:", error);
      toast({
        title: "Error Deleting Stream",
        description: `Failed to delete stream: ${error.message || "An unexpected error occurred."}`,
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setDeleteTarget(null);
    }
  };

  const handlePromoteGrade = () => {
    setActiveView("promote");
  };

  const handleBridgeToChange = async (gradeIndex: number, value: string) => {
    const updatedHierarchy = [...classHierarchy];
    const grade = updatedHierarchy[gradeIndex];

    let newNextGrade: string | undefined = grade.nextGrade;
    let newBridgeToGrade: string | undefined = grade.bridgeToGrade;
    let newBridgeToConvention: string | undefined = grade.bridgeToConvention;
    let newIsTerminal = grade.isTerminal;
    let newStageType: "final" | "progressive" | undefined;

    if (value === "graduate") {
      newIsTerminal = true;
      newStageType = "final";
      newBridgeToGrade = undefined;
      newBridgeToConvention = undefined;
      newNextGrade = undefined;
    } else {
      const targetConv = namingConventions.find((c) => c.id.toLowerCase() === value);
      if (targetConv) {
        newBridgeToConvention = targetConv.id;
        newBridgeToGrade = `${targetConv.name} ${targetConv.startLevel}`;
        newStageType = "progressive";
        newIsTerminal = false;
        newNextGrade = newBridgeToGrade;
      } else {
        return;
      }
    }

    grade.isTerminal = newIsTerminal;
    grade.nextGrade = newNextGrade;
    grade.bridgeToGrade = newBridgeToGrade;
    grade.bridgeToConvention = newBridgeToConvention;

    setClassHierarchy(updatedHierarchy);

    await saveBridgeToFirestore(gradeIndex, newStageType, newBridgeToGrade, newBridgeToConvention);
  };

  const handleSaveClass = async (newClassData: any) => {
    if (!schoolID) {
      toast({
        title: "Error",
        description: "School ID is missing. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    try {
      if (newClassData.grade && newClassData.stream) {
        const classRef = doc(db, `schools/${schoolID}/classes`, newClassData.stream.id);
        await updateDoc(classRef, {
          roomNumber: newClassData.stream.roomNumber || "",
          capacity: newClassData.stream.capacity || 0,
          teacher: newClassData.stream.teacher || null,
          teacherId: newClassData.stream.teacherId || null,
          currentClassTeacherUid: newClassData.stream.currentClassTeacherUid || null,
        });

        const updatedHierarchy = classHierarchy.map((grade) => {
          if (grade.grade === newClassData.grade) {
            return {
              ...grade,
              streams: grade.streams.map((stream) =>
                stream.id === newClassData.stream.id
                  ? { ...stream, ...newClassData.stream, classUid: stream.classUid }
                  : stream
              ),
            };
          }
          return grade;
        });

        setClassHierarchy(updatedHierarchy);
        toast({
          title: "Class Updated",
          description: `Class ${newClassData.grade} ${newClassData.stream.name} updated successfully.`,
          duration: 3000,
        });
      } else {
        const convention = namingConventions.find((c) => c.id === activeConvention?.id);
        const newGradeName = `${newClassData.className} ${newClassData.startingLevel}`;

        if (newClassData.isNewGrade || !classHierarchy.find((g) => g.grade === newGradeName)) {
          const newGrade: Grade = {
            grade: newGradeName,
            level: newClassData.startingLevel,
            conventionId: convention?.id || newClassData.className,
            isTerminal: newClassData.stage === "final",
            nextGrade: newClassData.stage === "progressive" ? `${newClassData.className} ${newClassData.startingLevel + 1}` : undefined,
            bridgeToGrade: newClassData.stage === "final" ? `${newClassData.className === "Grade" ? "Form" : "Grade"} 1` : undefined,
            isGraduated: false,
            streams: newClassData.streamNames.map((streamName: string, index: number) => ({
              id: newClassData.generatedClasses?.[index]?.classUid || Date.now() + index,
              classUid: newClassData.generatedClasses?.[index]?.classUid || Date.now() + index,
              name: streamName,
              roomNumber: "",
              capacity: 30, // Default capacity
              enrolled: 0,
              teacher: null,
              teacherId: null,
              currentClassTeacherUid: null,
              isGraduated: false,
            })),
          };
          setClassHierarchy([...classHierarchy, newGrade]);

          if (!namingConventions.find((c) => c.id === newClassData.className)) {
            const newConvention: NamingConvention = {
              id: newClassData.className,
              name: newClassData.className,
              prefix: newClassData.className,
              startLevel: newClassData.startingLevel,
              endLevel: newClassData.startingLevel + parseInt(newClassData.elevationLevel) - 1,
              isActive: false,
            };
            setNamingConventions([...namingConventions, newConvention]);
          } else {
            setNamingConventions(
              namingConventions.map((c) =>
                c.id === newClassData.className
                  ? { ...c, endLevel: Math.max(c.endLevel, newClassData.startingLevel + parseInt(newClassData.elevationLevel) - 1) }
                  : c
              )
            );
          }
        } else {
          const updatedHierarchy = classHierarchy.map((grade) => {
            if (grade.grade === newGradeName) {
              return {
                ...grade,
                streams: [
                  ...grade.streams,
                  ...newClassData.streamNames.map((streamName: string, index: number) => ({
                    id: newClassData.generatedClasses?.[index]?.classUid || Date.now() + index,
                    classUid: newClassData.generatedClasses?.[index]?.classUid || Date.now() + index,
                    name: streamName,
                    roomNumber: "",
                    capacity: 30, // Default capacity
                    enrolled: 0,
                    teacher: null,
                    teacherId: null,
                    currentClassTeacherUid: null,
                    isGraduated: false,
                  })),
                ],
              };
            }
            return grade;
          });
          setClassHierarchy(updatedHierarchy);
        }
      }

      setActiveView("list");
      setSelectedClass(null);
    } catch (error: any) {
      console.error("Error saving class:", error);
      toast({
        title: "Error Saving Class",
        description: `Failed to save class: ${error.message || "An unexpected error occurred."}`,
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleBack = () => {
    setActiveView("list");
    setSelectedClass(null);
  };

  const handleRetry = () => {
    setError(null);
    setLoading(true);
    setClassHierarchy([]);
    if (schoolID) {
      const classesCollectionRef = collection(db, `schools/${schoolID}/classes`);
      onSnapshot(classesCollectionRef, () => {}); // Trigger fetch
    }
  };

  const getCapacityColor = (enrolled: number, capacity: number) => {
    const percentage = capacity > 0 ? (enrolled / capacity) * 100 : 0;
    if (percentage >= 90) return "text-destructive";
    if (percentage >= 70) return "text-warning";
    return "text-success";
  };

  const getFilteredHierarchy = () => {
    return classHierarchy.filter((grade) => {
      const conventionMatch = grade.conventionId === activeConvention?.id;
      const gradeMatch = filterGrade === "all" || grade.grade === filterGrade;
      const streamMatch = filterStream === "all" || grade.streams.some((stream) => stream.name === filterStream);
      const searchMatch =
        searchTerm === "" ||
        grade.grade.toLowerCase().includes(searchTerm.toLowerCase()) ||
        grade.streams.some(
          (stream) =>
            stream.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (stream.teacher?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
            stream.roomNumber.toLowerCase().includes(searchTerm.toLowerCase())
        );

      return conventionMatch && gradeMatch && streamMatch && searchMatch;
    });
  };

  const isLastLevel = (grade: Grade) => {
    const convention = namingConventions.find((c) => c.id === grade.conventionId);
    return convention && grade.level === convention.endLevel;
  };

  const getConventionDisplay = (grade: Grade) => {
    return grade.grade;
  };

  const getAvailableConventions = () => {
    return namingConventions.filter((c) => classHierarchy.some((grade) => grade.conventionId === c.id));
  };

  const getAllGrades = () => {
    return classHierarchy.map((grade) => grade.grade);
  };

  const getAllStreams = (): string[] => {
    const streams = new Set<string>();
    classHierarchy.forEach((grade) => {
      grade.streams.forEach((stream) => streams.add(stream.name));
    });
    return Array.from(streams);
  };

  /* ── View Routing ──────────────────────────────────────────────── */
  if (activeView === "register") {
    return <ClassRegistrationForm onBack={handleBack} onSave={handleSaveClass} hierarchy={classHierarchy} />;
  }

  if (activeView === "edit" && selectedClass) {
    return (
      <ClassEditForm classData={selectedClass} onBack={handleBack} onSave={handleSaveClass} hierarchy={classHierarchy} />
    );
  }

  if (activeView === "students" && selectedClass) {
    return <StudentsTableView classData={selectedClass} onBack={handleBack} />;
  }

  if (activeView === "promote") {
    return <PromoteStudentsForm {...({ onBack: handleBack, hierarchy: classHierarchy } as any)} />;
  }

  /* ── Main UI ──────────────────────────────────────────────────── */
  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
        <AdminSidebar onSectionChange={() => {}} />
        <div className="flex flex-col flex-1 h-full">
          <Header />
          {loading && (
            <div className="flex-1 p-2 sm:p-4 lg:p-6 overflow-auto bg-background dark:bg-[#101828] flex justify-center items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          )}
          {error && (
            <div className="flex-1 p-2 sm:p-4 lg:p-6 overflow-auto bg-background dark:bg-[#101828] flex justify-center items-center flex-col gap-4">
              <p className="text-foreground text-center">{error}</p>
              <Button
                onClick={handleRetry}
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-2 px-4 rounded-full flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Retry
              </Button>
            </div>
          )}
          {!loading && !error && (
            <main className="flex-1 p-2 sm:p-4 lg:p-6 overflow-auto dark:bg-[#020817]">
              <div className="mb-4 sm:mb-6 ">
                <div className="flex flex-col gap-4 ">
                  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 ">
                    <div className="flex-1">
                      <h1 className="text-xl sm:text-2xl font-bold text-foreground mb-1 sm:mb-2">Classes Management</h1>
                      <p className="text-sm sm:text-base text-muted-foreground">
                        Manage class hierarchy, streams, and teacher assignments - {activeConvention?.name || "No Convention"} System
                      </p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
                      {namingConventions.length > 0 && getAvailableConventions().length > 1 && (
                        <Select
                          value={activeConvention?.id ?? ""}
                          onValueChange={(value) => {
                            const convention = namingConventions.find((c) => c.id === value);
                            if (convention) setActiveConvention(convention);
                          }}
                        >
                          <SelectTrigger className="w-full sm:w-40 font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                            <SelectValue placeholder="Select convention" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                            {getAvailableConventions().map((convention) => (
                              <SelectItem key={convention.id} value={convention.id} className="hover:bg-gray-50 dark:hover:bg-[#101828]">
                                {convention.name} System
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <Button 
                        onClick={handleRegisterClass} 
                        className="bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6] flex items-center gap-2 text-sm sm:text-base transition-colors"
                      >
                        <Plus className="h-4 w-4" />
                        <span className="hidden sm:inline">Register Class</span>
                        <span className="sm:hidden">Add Class</span>
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handlePromoteGrade}
                        className="flex items-center gap-2 text-sm sm:text-base"
                      >
                        <TrendingUp className="h-4 w-4" />
                        <span className="hidden sm:inline">Promote Students</span>
                        <span className="sm:hidden">Promote</span>
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                      placeholder="Search grades, streams, teachers..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 text-sm"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Select value={filterGrade} onValueChange={setFilterGrade}>
                      <SelectTrigger className="w-full sm:w-32 font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                        <SelectValue placeholder="Grade" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                        <SelectItem value="all" className="hover:bg-gray-50 dark:hover:bg-[#101828]">All Grades</SelectItem>
                        {getAllGrades().map((grade) => (
                          <SelectItem key={grade} value={grade} className="hover:bg-gray-50 dark:hover:bg-[#101828]">{grade}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={filterStream} onValueChange={setFilterStream}>
                      <SelectTrigger className="w-full sm:w-32 font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                        <SelectValue placeholder="Stream" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                        <SelectItem value="all" className="hover:bg-gray-50 dark:hover:bg-[#101828]">All Streams</SelectItem>
                        {getAllStreams().map((stream) => (
                          <SelectItem key={stream} value={stream} className="hover:bg-gray-50 dark:hover:bg-[#101828]">{stream}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="space-y-4 sm:space-y-6">
                {getFilteredHierarchy().map((grade, gradeIndex) => {
                  const realGradeIndex = classHierarchy.findIndex(g => g === grade);
                  return (
                  <Card key={grade.grade} className="m-2 bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl overflow-hidden hover:bg-gray-50 ">
                    <CardHeader className="pb-3">
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                            <School className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                          </div>
                          <div>
                            <h2 className="text-lg sm:text-xl font-semibold text-foreground">{getConventionDisplay(grade)}</h2>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              {grade.isGraduated && (
                                <Badge variant="outline" className="text-xs border-green-500 text-green-600">
                                  <GraduationCap className="h-3 w-3 mr-1" />
                                  Graduated
                                </Badge>
                              )}
                              <Badge 
                                variant="outline" 
                                className={`text-xs px-2.5 py-0.5 border font-medium ${
                                  grade.isTerminal 
                                    ? "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800" 
                                    : "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-100 dark:border-blue-800"
                                }`}
                              >
                                {grade.isTerminal ? "Terminal Grade" : `Next: ${grade.nextGrade}`}
                              </Badge>
                              {grade.bridgeToGrade && (
                                <Badge variant="outline" className="text-xs">
                                  <ArrowRight className="h-3 w-3 mr-1" />
                                  Bridges to {grade.bridgeToGrade}
                                </Badge>
                              )}
                              {isLastLevel(grade) && grade.isTerminal && !grade.isGraduated && (
                                <Badge variant="outline" className="text-xs border-yellow-500 text-yellow-600">
                                  <GraduationCap className="h-3 w-3 mr-1" />
                                  Final Level
                                </Badge>
                              )}
                              {isLastLevel(grade) && !grade.isGraduated && (
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">Bridge to:</span>
                                  <Select
                                    value={
                                      grade.isTerminal
                                        ? "graduate"
                                        : grade.bridgeToConvention
                                        ? grade.bridgeToConvention.toLowerCase()
                                        : ""
                                    }
                                    onValueChange={(value) => handleBridgeToChange(realGradeIndex, value)}
                                  >
                                    <SelectTrigger className="w-24 h-6 text-xs">
                                      <SelectValue placeholder="Select" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                                      {namingConventions.filter((c) => c.id !== grade.conventionId).map((conv) => (
                                        <SelectItem
                                          key={conv.id}
                                          value={conv.id.toLowerCase()}
                                          className="hover:bg-gray-50 dark:hover:bg-[#101828]"
                                        >
                                          {conv.name} System
                                        </SelectItem>
                                      ))}
                                      <SelectItem value="graduate" className="hover:bg-gray-50 dark:hover:bg-[#101828]">
                                        Graduate (Final)
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleAddStream(gradeIndex)}
                            size="sm"
                            variant="outline"
                            className="flex items-center gap-1 text-xs sm:text-sm"
                            disabled={grade.isGraduated}
                          >
                            <Plus className="h-3 w-3" />
                            Add Stream
                          </Button>
                          {isLastLevel(grade) && !grade.isGraduated && (
                            <Button
                              onClick={() => handleGraduateGrade(realGradeIndex)}
                              size="sm"
                              variant="default"
                              className="flex items-center gap-1 text-xs sm:text-sm bg-green-600 hover:bg-green-700"
                            >
                              <GraduationCap className="h-3 w-3" />
                              Graduate Class
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                        {grade.streams.map((stream, streamIndex) => (
                          <Card
                            key={stream.id}
                            className={`m-2 bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl overflow-hidden hover:bg-gray-50 dark:hover:bg-[#101828] transition-all duration-200 cursor-pointer ${stream.isGraduated ? "opacity-60" : ""}`}
                            onClick={() => handleViewStudents(getConventionDisplay(grade), stream)}
                          >
                            <CardHeader className="pb-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                                    <span className="text-xs sm:text-sm font-medium text-primary">
                                      {stream.name.charAt(0)}
                                    </span>
                                  </div>
                                  <div>
                                    <h3 className="font-medium text-sm sm:text-base text-foreground">
                                      {getConventionDisplay(grade)} {stream.name}
                                    </h3>
                                    <p className="text-xs sm:text-sm text-muted-foreground">
                                      Room {stream.roomNumber || "TBA"}
                                    </p>
                                  </div>
                                </div>
                                {stream.isGraduated && (
                                  <Badge variant="outline" className="text-xs border-green-500 text-green-600">
                                    <GraduationCap className="h-3 w-3 mr-1" />
                                    Graduated
                                  </Badge>
                                )}
                              </div>
                            </CardHeader>
                            <CardContent className="pt-2">
                              <div className="space-y-2 sm:space-y-3">
                                <div className="flex items-center gap-2 text-xs sm:text-sm">
                                  <BookOpen className="h-3 w-3 text-muted-foreground" />
                                  <span className="text-muted-foreground truncate">
                                    {stream.teacher || "No teacher assigned"}
                                  </span>
                                </div>

                                <div className="flex justify-between items-center">
                                  <span className="text-xs sm:text-sm text-muted-foreground">Enrollment</span>
                                  <span className={`text-xs sm:text-sm font-medium ${getCapacityColor(stream.enrolled, stream.capacity)}`}>
                                    {enrollmentLoading ? "..." : `${stream.enrolled}/${stream.capacity}`}
                                  </span>
                                </div>

                                <div className="w-full bg-secondary rounded-full h-1.5 sm:h-2">
                                  <div
                                    className="h-1.5 sm:h-2 rounded-full transition-all duration-300"
                                    style={{
                                      width: enrollmentLoading ? "0%" : `${(stream.enrolled / stream.capacity) * 100}%`,
                                      background: '#3B82F6'
                                    }}
                                  ></div>
                                </div>

                                <div className="flex justify-between text-xs text-muted-foreground">
                                  <span>Available: {enrollmentLoading ? "..." : stream.capacity - stream.enrolled}</span>
                                </div>
                              </div>

                              <div className="flex flex-wrap gap-1 sm:gap-2 mt-3 sm:mt-4 pt-3 border-t border-border/50">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewStudents(getConventionDisplay(grade), stream);
                                  }}
                                  className="flex items-center gap-1 text-xs flex-1 sm:flex-none"
                                  disabled={stream.isGraduated}
                                >
                                  <Users className="h-3 w-3" />
                                  Students
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditStream(getConventionDisplay(grade), stream);
                                  }}
                                  className="flex items-center gap-1 text-xs flex-1 sm:flex-none"
                                  disabled={stream.isGraduated}
                                >
                                  <Edit className="h-3 w-3" />
                                  Edit
                                </Button>
                                {isLastLevel(grade) && !stream.isGraduated && (
                                  <Button
                                    size="sm"
                                    variant="default"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleGraduateStream(realGradeIndex, streamIndex);
                                    }}
                                    className="flex items-center gap-1 text-xs bg-green-600 hover:bg-green-700"
                                  >
                                    <GraduationCap className="h-3 w-3" />
                                    <span className="hidden sm:inline">Graduate</span>
                                  </Button>
                                )}
                                {grade.streams.length > 1 && !stream.isGraduated && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteStream(realGradeIndex, streamIndex);
                                    }}
                                    className="flex items-center gap-1 text-xs text-destructive hover:text-destructive-foreground hover:bg-destructive"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                    <span className="hidden sm:inline">Delete</span>
                                  </Button>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )})}
              </div>

              {getFilteredHierarchy().length === 0 && (
                <div className="text-center py-8 sm:py-12">
                  <School className="h-12 w-12 sm:h-16 sm:w-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg sm:text-xl font-medium text-foreground mb-2">No classes found</h3>
                  <p className="text-sm sm:text-base text-muted-foreground mb-4">
                    No classes match your search criteria. Try adjusting your filters.
                  </p>
                  <Button onClick={() => { setSearchTerm(""); setFilterGrade("all"); setFilterStream("all"); }}>
                    Clear Filters
                  </Button>
                </div>
              )}

              <Dialog open={isAddStreamDialogOpen} onOpenChange={setIsAddStreamDialogOpen}>
                <DialogContent className="sm:max-w-[425px] bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                  <DialogHeader>
                    <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-white">
                      Add New Stream
                    </DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                      <label
                        htmlFor="streamName"
                        className="text-sm font-medium text-gray-900 dark:text-white col-span-1"
                      >
                        Stream Name
                      </label>
                      <Input
                        id="streamName"
                        value={newStreamName}
                        onChange={(e) => setNewStreamName(e.target.value)}
                        placeholder="e.g., Green, Red, Alpha"
                        className="col-span-3 bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-900 dark:text-white"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleAddStreamSubmit();
                          }
                        }}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsAddStreamDialogOpen(false);
                        setNewStreamName("");
                      }}
                      className="border-[#E2E8F0] dark:border-[#1E293B] text-gray-900 dark:text-white"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handleAddStreamSubmit}
                      className="bg-blue-600 ml-2 text-white hover:bg-blue-700"
                    >
                      Add Stream
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogContent className="sm:max-w-[425px] bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                  <DialogHeader>
                    <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-white">
                      Confirm Stream Deletion
                    </DialogTitle>
                    <DialogDescription className="text-sm text-gray-600 dark:text-gray-400">
                      Are you sure you want to delete the stream "{deleteTarget?.stream?.name}" in{" "}
                      {deleteTarget && classHierarchy[deleteTarget.gradeIndex].grade}? This action will remove the class from
                      the system and unassign any students currently enrolled in it. This cannot be undone.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsDeleteDialogOpen(false);
                        setDeleteTarget(null);
                      }}
                      className="border-[#E2E8F0] dark:border-[#1E293B] text-gray-900 dark:text-white"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={confirmDeleteStream}
                      className="bg-red-600 text-white hover:bg-red-700"
                    >
                      Delete Stream
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </main>
          )}
        </div>
      </div>
    </SidebarProvider>
  );
};

export default ClassesManagement;